import { MAX_MAP_PLOT_REQUEST_DETAILS } from "@/constants/mapPlotRequest";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  Box,
  Typography,
  Link,
  CircularProgress,
  TextField,
  DialogActions,
} from "@mui/material";
import { useState, useEffect } from "react";
import { Button, ButtonVariantOption } from "@/components/Button";
import { useApiClient } from "@/hooks/useApiClient";
import { toast } from "react-toastify";
import { HttpStatusCode, isAxiosError } from "axios";
import { FileUploadButton } from "@/features/realEstateReceptionBookFeed/components/FileUploadButton";
import { readFile } from "@/utils/readFile";
import { Label } from "@/components/Label";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFeatureFlags } from "@/configs/featureFlag";

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  modalTitle: string;
  confirmButtonLabel: string;
  apiEndpoint: string;
  onUploadSuccess: () => void;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const MAX_CSV_ROWS = MAX_MAP_PLOT_REQUEST_DETAILS;

// Zodスキーマ定義
const FormDataSchema = z.object({
  name: z
    .string()
    .min(1, "識別名は必須です")
    .max(255, "識別名は255文字以内で入力してください"),
});

// 定義したZodのスキーマをTypescriptの型に変換
type FormData = z.infer<typeof FormDataSchema>;

export const MapPlotRequestCsvUploadModal: React.FC<Props> = ({
  open,
  setOpen,
  modalTitle,
  confirmButtonLabel,
  apiEndpoint,
  onUploadSuccess,
  loading,
  setLoading,
}) => {
  const { crm: crmFeatureFlag } = useFeatureFlags();
  const crm = Boolean(crmFeatureFlag);
  const sampleCsvPath = crm
    ? "/assets/crm/sample_地図プロットサンプル.csv"
    : "/assets/non_crm/sample_地図プロットサンプル.csv";
  const sampleExcelPath = crm
    ? "/assets/crm/sample_地図プロットサンプル.xlsx"
    : "/assets/non_crm/sample_地図プロットサンプル.xlsx";

  const [fileToCsvUpload, setFileToCsvUpload] = useState<File | null>(null);
  const [showFileErrorMessage, setShowFileErrorMessage] =
    useState<boolean>(false);
  const { apiClient } = useApiClient();

  // React Hook Form の初期化
  const { control, handleSubmit, reset, watch } = useForm<FormData>({
    mode: "all",
    defaultValues: {
      name: "",
    },
    resolver: zodResolver(FormDataSchema),
  });

  // 識別名の値を監視
  const nameValue = watch("name");

  // 保存ボタンの有効状態
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);

  // nameValueとfileToCsvUploadの状態が変わったら保存ボタンの有効状態を更新
  useEffect(() => {
    setIsButtonDisabled(!fileToCsvUpload || loading || !nameValue.trim());
  }, [fileToCsvUpload, loading, nameValue]);

  const handleClose = (): void => {
    setOpen(false);
    setFileToCsvUpload(null);
    setShowFileErrorMessage(false);
    reset(); // フォームの値をリセット
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    setShowFileErrorMessage(false);
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      try {
        const fileData = await readFile(file);
        if (fileData.data.length > MAX_CSV_ROWS) {
          setShowFileErrorMessage(true);
          setFileToCsvUpload(null);
          return;
        }
        setFileToCsvUpload(file);
      } catch (error) {
        console.error(error);
        toast.error("ファイルの読み込みに失敗しました");
        setFileToCsvUpload(null);
      }
    }
  };

  const handleFileChangeWrapper = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    void handleFileChange(event);
  };

  const onSubmit = (data: FormData): void => {
    if (!fileToCsvUpload) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("file", fileToCsvUpload);
    formData.append("name", data.name);

    apiClient
      .post(apiEndpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        onUploadSuccess();
        setLoading(false);
        handleClose();
      })
      .catch((error: Error) => {
        setLoading(false);
        console.error(error);
        if (
          isAxiosError(error) &&
          error.response?.status === HttpStatusCode.BadRequest
        ) {
          let errorMessage = "ファイル内容をご確認ください。";

          try {
            const responseData = error.response?.data as { file?: unknown };
            if (
              responseData?.file &&
              Array.isArray(responseData.file) &&
              responseData.file.length > 0 &&
              typeof responseData.file[0] === "string"
            ) {
              errorMessage = responseData.file[0];
            }
          } catch (e) {
            console.error("エラーメッセージの解析に失敗しました", e);
          }

          toast.error(
            <div>
              ファイルのアップロードに失敗しました。
              <br />
              {errorMessage}
            </div>
          );
        } else {
          toast.error("ファイルのアップロードに失敗しました");
        }
      });
  };

  // ボタンクリック時のハンドラー
  const handleButtonClick = (): void => {
    void handleSubmit(onSubmit)();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(
          event: Record<string, unknown>,
          reason: "backdropClick" | "escapeKeyDown"
        ) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleClose();
        }}
        fullWidth={true}
        maxWidth={"md"}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{modalTitle}</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Link href={sampleCsvPath}>見本CSVをダウンロード</Link>
            <Link href={sampleExcelPath}>見本Excelをダウンロード</Link>
          </Stack>
          <Stack alignItems="center">
            <Box>
              <Typography variant="h6">
                CSVまたはエクセルをアップロードする
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                mt: 2,
                width: "100%",
                maxWidth: "300px",
              }}
            >
              <Box sx={{ width: "100%" }}>
                <Label required={true}>識別名</Label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="識別名"
                      variant="outlined"
                      inputProps={{ maxLength: 255 }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Box>
              <FileUploadButton
                variant={ButtonVariantOption.Outlined}
                onChange={handleFileChangeWrapper}
                accept={".csv, .xlsx, .xls"}
                label={"アップロード"}
              />
              {fileToCsvUpload && (
                <Typography>{fileToCsvUpload.name}</Typography>
              )}
              {showFileErrorMessage && (
                <Typography color="error">
                  {`ファイルの行数は${MAX_CSV_ROWS.toLocaleString()}以下にしてください。`}
                </Typography>
              )}
            </Box>
          </Stack>

          <Box sx={{ mt: 3, width: "100%" }}>
            <Typography variant="caption" color="text.secondary">
              ※ 1つの物件リストに保存できる物件は最大で
              {MAX_CSV_ROWS.toLocaleString()}件です
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              ※
              データの登録はバックグラウンドで処理されます。処理完了後にメールでお知らせが届きましたら、データを取得できます
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", mb: 1 }}>
          <Button
            variant={ButtonVariantOption.Outlined}
            onClick={handleClose}
            label={"キャンセル"}
          />
          <Button
            variant={ButtonVariantOption.Contained}
            onClick={handleButtonClick}
            disabled={isButtonDisabled}
            label={confirmButtonLabel}
          />
        </DialogActions>
      </Dialog>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </>
  );
};
